import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BookOpen, Pencil, Plus, Search, Trash2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const emptyEditor = { name: '', ingredients: '', preparation: '' };

const RecipeLibrary = ({ departmentId, canEdit = false, onClose }) => {
  const [recipes, setRecipes] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadRecipes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/departments/${departmentId}/recipes`);
      setRecipes(response.data || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Die Rezepte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [departmentId]);

  useEffect(() => { loadRecipes(); }, [loadRecipes]);

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('de-DE');
    return query
      ? recipes.filter(recipe => recipe.name.toLocaleLowerCase('de-DE').includes(query))
      : recipes;
  }, [recipes, search]);

  const beginEdit = (recipe = null) => {
    setEditor(recipe ? {
      id: recipe.id,
      name: recipe.name,
      ingredients: (recipe.ingredients || []).join('\n'),
      preparation: recipe.preparation || '',
    } : { ...emptyEditor });
    setError('');
  };

  const save = async (event) => {
    event.preventDefault();
    if (!editor || saving) return;
    const payload = {
      name: editor.name.trim(),
      ingredients: editor.ingredients.split('\n').map(line => line.trim()).filter(Boolean),
      preparation: editor.preparation.trim() || null,
    };
    if (!payload.name || payload.ingredients.length === 0) {
      setError('Name und mindestens eine Zutat sind erforderlich.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = editor.id
        ? await axios.put(`${API}/department-admin/${departmentId}/recipes/${editor.id}`, payload)
        : await axios.post(`${API}/department-admin/${departmentId}/recipes`, payload);
      setRecipes(current => editor.id
        ? current.map(recipe => recipe.id === editor.id ? response.data : recipe)
        : [...current, response.data]);
      setSelected(response.data);
      setEditor(null);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Das Rezept konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (recipe) => {
    if (!window.confirm(`Rezept „${recipe.name}“ wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/department-admin/${departmentId}/recipes/${recipe.id}`);
      setRecipes(current => current.filter(item => item.id !== recipe.id));
      setSelected(null);
      setEditor(null);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Das Rezept konnte nicht gelöscht werden.');
    }
  };

  return (
    <section className="w-full rounded-2xl bg-white shadow-xl" aria-label="Rezepte">
      <header className="flex items-start justify-between border-b border-gray-100 p-4 sm:p-6">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <BookOpen className="h-5 w-5 shrink-0 text-gray-700" aria-hidden="true" />
            <span>Rezepte</span>
          </h2>
          <p className="mt-1 text-sm text-gray-500">So kocht eure Wachabteilung</p>
        </div>
        <div className="flex gap-2">
          {canEdit && <button type="button" data-testid="new-recipe" onClick={() => beginEdit()} className="flex min-h-11 items-center gap-2 rounded-xl bg-gray-800 px-4 font-semibold text-white hover:bg-gray-900"><Plus className="h-4 w-4" /> Neues Rezept</button>}
          {onClose && <button type="button" onClick={onClose} aria-label="Rezepte schließen" className="flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X /></button>}
        </div>
      </header>

      {error && <p role="alert" className="m-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {editor ? (
        <form onSubmit={save} className="space-y-4 p-4 sm:p-6">
          <label className="block text-sm font-semibold text-gray-700">Name<input name="recipe-name" value={editor.name} onChange={event => setEditor(current => ({ ...current, name: event.target.value }))} className="mt-1 min-h-11 w-full rounded-xl border border-gray-300 px-3" /></label>
          <label className="block text-sm font-semibold text-gray-700">Zutaten – eine pro Zeile<textarea name="recipe-ingredients" value={editor.ingredients} onChange={event => setEditor(current => ({ ...current, ingredients: event.target.value }))} rows="7" className="mt-1 w-full rounded-xl border border-gray-300 p-3" /></label>
          <label className="block text-sm font-semibold text-gray-700">Zubereitung (optional)<textarea name="recipe-preparation" value={editor.preparation} onChange={event => setEditor(current => ({ ...current, preparation: event.target.value }))} rows="5" className="mt-1 w-full rounded-xl border border-gray-300 p-3" /></label>
          <div className="flex justify-end gap-3"><button type="button" onClick={() => setEditor(null)} className="min-h-11 rounded-xl border px-4">Abbrechen</button><button type="submit" disabled={saving} className="min-h-11 rounded-xl bg-gray-800 px-5 font-semibold text-white hover:bg-gray-900">Rezept speichern</button></div>
        </form>
      ) : selected ? (
        <article className="p-4 sm:p-6">
          <button type="button" onClick={() => setSelected(null)} className="mb-4 text-sm font-medium text-gray-600 hover:text-gray-900">← Zur Übersicht</button>
          <div className="flex items-start justify-between gap-3"><h3 className="text-2xl font-bold text-gray-900">{selected.name}</h3>{canEdit && <div className="flex gap-2"><button type="button" onClick={() => beginEdit(selected)} className="min-h-11 rounded-lg p-2 text-blue-700" aria-label={`${selected.name} bearbeiten`}><Pencil /></button><button type="button" onClick={() => remove(selected)} className="min-h-11 rounded-lg p-2 text-red-700"> <Trash2 /><span className="sr-only">Rezept löschen</span></button></div>}</div>
          <h4 className="mt-6 font-semibold text-gray-700">Das gehört hinein</h4>
          <ul className="mt-2 list-disc space-y-2 pl-5 text-gray-800">{(selected.ingredients || []).map((ingredient, index) => <li key={`${ingredient}-${index}`}>{ingredient}</li>)}</ul>
          {selected.preparation && <><h4 className="mt-6 font-semibold text-gray-700">Zubereitung</h4><p className="mt-2 whitespace-pre-wrap text-gray-800">{selected.preparation}</p></>}
        </article>
      ) : (
        <div className="p-4 sm:p-6">
          <label className="relative block"><Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Rezepte durchsuchen" className="min-h-11 w-full rounded-xl border border-gray-300 pl-10 pr-3" /></label>
          {loading ? <p className="py-10 text-center text-gray-500">Rezepte werden geladen …</p> : filtered.length === 0 ? <p className="py-10 text-center text-gray-500">Noch keine Rezepte hinterlegt</p> : <ul className="mt-5 divide-y divide-gray-100" role="list">{filtered.map(recipe => <li key={recipe.id}><button type="button" data-testid={`recipe-${recipe.id}`} onClick={() => setSelected(recipe)} className="flex min-h-14 w-full items-center justify-between px-2 py-4 text-left font-semibold text-gray-900 transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"><span>{recipe.name}</span><span aria-hidden="true" className="text-sm font-normal text-gray-400">Öffnen →</span></button></li>)}</ul>}
        </div>
      )}
    </section>
  );
};

export default RecipeLibrary;
